import { useMemo, useState } from 'react';
import { humanizeEnum } from '../../lib/fieldLabels.js';
import {
  ARCHIVE_APPROVAL_STATUSES,
  ARCHIVE_EVENT_TYPES,
  ARCHIVE_SOURCE_TYPES,
  ARCHIVE_TIME_WINDOWS,
  filterProjectArchiveEvents,
  getProjectArchiveDemoData,
  summarizeProjectArchiveWindows,
} from './projectArchiveDemoData.js';

const FILTER_DEFAULTS = Object.freeze({
  query: '',
  eventType: 'all',
  sourceType: 'all',
  approvalStatus: 'all',
  timeWindow: 'all',
});

const EVENT_TYPE_LABELS = Object.freeze({
  communication: 'Communication',
  approval: 'Approval',
  delivery: 'Delivery',
  financial: 'Financial',
  risk: 'Risk',
  gate: 'Gate',
  document: 'Document',
  stakeholder: 'Stakeholder',
  system: 'System',
});

const SOURCE_TYPE_LABELS = Object.freeze({
  email: 'Email',
  meeting: 'Meeting',
  ado: 'ADO',
  document: 'Document',
  gate: 'Gate',
  finance: 'Finance',
  manual: 'Manual',
});

const APPROVAL_LABELS = Object.freeze({
  approved: 'Approved',
  'pending-review': 'Pending review',
  rejected: 'Rejected',
  'not-required': 'Not required',
});

const EVENT_MARKERS = Object.freeze({
  communication: 'CM',
  approval: 'AP',
  delivery: 'DL',
  financial: 'FN',
  risk: 'RK',
  gate: 'GT',
  document: 'DC',
  stakeholder: 'ST',
  system: 'SY',
});

function formatArchiveDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || 'Date unavailable';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function formatConfidence(value) {
  if (typeof value !== 'number') return 'Confidence not scored';
  return `${Math.round(value * 100)}% confidence`;
}

function impactTone(impact) {
  if (impact === 'high') return 'brick';
  if (impact === 'medium') return 'amber';
  return 'sage';
}

function approvalTone(status) {
  if (status === 'approved') return 'sage';
  if (status === 'pending-review') return 'amber';
  if (status === 'rejected') return 'brick';
  return 'outline';
}

function joinLabels(keys, labels) {
  if (!keys.length) return 'No dominant type';
  return keys.map((key) => labels[key] || key).join(', ');
}

export function ProjectArchiveTab({ project }) {
  const [filters, setFilters] = useState(FILTER_DEFAULTS);
  const archive = useMemo(() => getProjectArchiveDemoData(project), [project]);
  const archivedProject = project?.status === 'archived' || project?.lifecycleStage === 'archived';
  const summaries = useMemo(() => summarizeProjectArchiveWindows(archive.events), [archive.events]);
  const visibleEvents = useMemo(
    () => filterProjectArchiveEvents(archive.events, filters),
    [archive.events, filters],
  );

  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));
  const resetFilters = () => setFilters(FILTER_DEFAULTS);
  const hasActiveFilters = Object.entries(filters).some(
    ([key, value]) => value !== FILTER_DEFAULTS[key],
  );

  return (
    <div className="profile-tab project-archive-shell" data-testid="project-archive-tab">
      <section className="project-archive-hero">
        <div>
          <div className="eyebrow">Corporate memory summary</div>
          <h3>Corporate memory</h3>
          <p className="muted">
            Generated summary for {archive.projectName}. This narrative summary preserves the
            running product history, evidence cues, and decision context without changing live
            workflow state.
          </p>
          {archivedProject ? (
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              <span className="chip sage sm">Archived project</span>
              <span className="chip outline sm">Lifecycle archived</span>
            </div>
          ) : null}
        </div>
        <div className="project-archive-posture" aria-label="Archive data posture">
          <span className="chip blue sm">Illustrative data</span>
          <span className="chip outline sm">Read-only archive</span>
        </div>
      </section>

      <section
        className="project-archive-summary-grid"
        data-testid="project-archive-summary-grid"
        aria-label="Archive summary windows"
      >
        {summaries.map((summary) => (
          <article key={summary.key} className="project-archive-summary-card">
            <div className="row spread project-archive-summary-heading">
              <span className="eyebrow">{summary.label}</span>
              <span className="project-archive-count">{summary.eventCount}</span>
            </div>
            <strong>{joinLabels(summary.dominantEventTypes, EVENT_TYPE_LABELS)}</strong>
            <p>{summary.highlight}</p>
            <div className="project-archive-summary-cues">
              <span className={`chip ${approvalTone(summary.approvalCue)} sm`}>
                {APPROVAL_LABELS[summary.approvalCue] || summary.approvalCue}
              </span>
              <span className="chip outline sm">{summary.versionCue}</span>
            </div>
          </article>
        ))}
      </section>

      <section className="project-archive-filters" aria-label="Archive filters">
        <label className="project-archive-field project-archive-search-field">
          <span>Search archive</span>
          <div className="project-archive-search-control">
            <input
              aria-label="Search archive"
              type="search"
              value={filters.query}
              onChange={(event) => setFilter('query', event.target.value)}
              placeholder="Search timeline, actors, citations"
            />
            {filters.query ? (
              <button type="button" className="btn ghost sm" onClick={() => setFilter('query', '')}>
                Clear
              </button>
            ) : null}
          </div>
        </label>

        <label className="project-archive-field">
          <span>Event type</span>
          <select
            aria-label="Event type"
            value={filters.eventType}
            onChange={(event) => setFilter('eventType', event.target.value)}
          >
            <option value="all">All event types</option>
            {ARCHIVE_EVENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {EVENT_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </label>

        <label className="project-archive-field">
          <span>Source type</span>
          <select
            aria-label="Source type"
            value={filters.sourceType}
            onChange={(event) => setFilter('sourceType', event.target.value)}
          >
            <option value="all">All source types</option>
            {ARCHIVE_SOURCE_TYPES.map((type) => (
              <option key={type} value={type}>
                {SOURCE_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </label>

        <label className="project-archive-field">
          <span>Approval status</span>
          <select
            aria-label="Approval status"
            value={filters.approvalStatus}
            onChange={(event) => setFilter('approvalStatus', event.target.value)}
          >
            <option value="all">All approval cues</option>
            {ARCHIVE_APPROVAL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {APPROVAL_LABELS[status]}
              </option>
            ))}
          </select>
        </label>

        <label className="project-archive-field">
          <span>Time window</span>
          <select
            aria-label="Time window"
            value={filters.timeWindow}
            onChange={(event) => setFilter('timeWindow', event.target.value)}
          >
            <option value="all">All windows</option>
            {ARCHIVE_TIME_WINDOWS.map((window) => (
              <option key={window.key} value={window.key}>
                {window.label}
              </option>
            ))}
          </select>
        </label>
      </section>

      <div className="row spread project-archive-result-bar">
        <span className="muted small">
          Showing {visibleEvents.length} of {archive.events.length} archive entries
        </span>
        {hasActiveFilters && visibleEvents.length > 0 ? (
          <button type="button" className="btn outline sm" onClick={resetFilters}>
            Reset filters
          </button>
        ) : null}
      </div>

      {visibleEvents.length === 0 ? (
        <section className="project-archive-empty">
          <h4>No archive entries match those filters</h4>
          <p className="muted">Filters and search removed every archive entry for this project.</p>
          <button type="button" className="btn outline sm" onClick={resetFilters}>
            Reset filters
          </button>
        </section>
      ) : (
        <section
          className="project-archive-timeline"
          data-testid="project-archive-timeline"
          aria-label="Archive timeline"
        >
          {visibleEvents.map((event) => (
            <article
              key={event.id}
              className={`project-archive-event-card archive-event-${event.eventType}`}
              data-testid="project-archive-event-card"
            >
              <div className="project-archive-event-marker" aria-hidden>
                {EVENT_MARKERS[event.eventType] || 'AR'}
              </div>
              <div className="project-archive-event-body">
                <header className="project-archive-event-header">
                  <div>
                    <time dateTime={event.occurredAt}>{formatArchiveDate(event.occurredAt)}</time>
                    <h4>{event.title}</h4>
                  </div>
                  <div className="project-archive-chip-stack" aria-label="Archive cues">
                    <span className="chip outline sm">{EVENT_TYPE_LABELS[event.eventType]}</span>
                    <span className="chip outline sm">{SOURCE_TYPE_LABELS[event.sourceType]}</span>
                    {event.approvalStatus ? (
                      <span className={`chip ${approvalTone(event.approvalStatus)} sm`}>
                        {APPROVAL_LABELS[event.approvalStatus]}
                      </span>
                    ) : null}
                  </div>
                </header>
                <p className="project-archive-event-summary">{event.summary}</p>
                <div className="project-archive-event-meta">
                  <span>
                    <strong>{event.actor}</strong>
                    {event.organization ? ` - ${event.organization}` : ''}
                  </span>
                  <span className={`chip ${impactTone(event.impact)} sm`}>
                    {event.impact || 'low'} impact
                  </span>
                  <span className="chip outline sm">{formatConfidence(event.confidence)}</span>
                  {event.versionLabel ? (
                    <span className="chip outline sm">{event.versionLabel}</span>
                  ) : null}
                </div>
                <div className="project-archive-citations" aria-label="Citation source excerpts">
                  {event.citations.map((citation) => (
                    <figure
                      key={`${event.id}-${citation.label}`}
                      className="project-archive-citation"
                    >
                      <figcaption>
                        <span>Source excerpt</span>
                        <strong>{citation.label}</strong>
                      </figcaption>
                      <blockquote>{citation.excerpt}</blockquote>
                      <span className="chip outline sm">
                        {SOURCE_TYPE_LABELS[citation.sourceType] || humanizeEnum(citation.sourceType)}
                      </span>
                    </figure>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
